import Checklist, { DependentsInfoSupportingItemInput } from 'jobs/Checklist'
import InfoGatheringStore from 'jobs/InfoGatheringStore'
import Job from 'jobs/Job'
import { computed, makeObservable } from 'mobx'
import { toStream } from 'utils/mobx-util'
import { switchMap } from 'rxjs/operators'
import { whileActive, withCancellation } from 'utils/rx-util'
import { CancellationToken } from 'infra/cancellation'
import { task } from 'mobx-task'
import IndividualProfileStore from '../IndividualProfileStore'
import { BehaviorSubject } from 'rxjs'

export default class DependentProfileViewModel {
  // Tracks whether the view model is active.
  private readonly _active = new BehaviorSubject<boolean>(false)

  constructor(
    public job: Job,
    public infoGatheringStore: InfoGatheringStore,
    public individualProfileStore: IndividualProfileStore,
    private getChecklist: () => Checklist | undefined,
  ) {
    makeObservable(this)

    // reacts to changes in the supporting item to re-fetch the profile.
    toStream(() => {
      return {
        dependents: this.dependentInfoSupportingItemInput?.dependents,
      }
    }, true)
      .pipe(
        whileActive(this._active),
        switchMap(() => withCancellation(this._onRemoteItemUpdated)),
      )
      .subscribe()
  }

  /**
   * The list of dependent profiles.
   */
  @computed
  get dependentProfiles() {
    return (
      this.dependentInfoSupportingItemInput?.dependents
        ?.filter((d) => d.profileVersion)
        ?.map((d) =>
          this.individualProfileStore.getDependentProfile(
            d.profileId,
            d.profileVersion!,
          ),
        ) ?? []
    )
  }

  /**
   * The checklist.
   */
  @computed
  get checklist() {
    return this.getChecklist()
  }

  /**
   * The personal info section.
   */
  @computed
  get personalInfoSection() {
    return this.checklist?.personalInfoSection
  }

  /**
   * The supporting item.
   */
  @computed
  get dependentInfoSupportingItem() {
    return this.personalInfoSection?.dependentInfoSupportingItem
  }

  /**
   * The supporting item input.
   */
  @computed
  get dependentInfoSupportingItemInput() {
    return this.dependentInfoSupportingItem
      ?.input as DependentsInfoSupportingItemInput
  }

  /**
   * Activates the vm.
   */
  activate = task.resolved(async () => {
    this._active.next(true)

    if (this.dependentInfoSupportingItem?.completed) {
      await this.fetchDependentProfiles()
    }
  })

  /**
   * Fetches the dependent profiles.
   */
  async fetchDependentProfiles() {
    if (this.dependentInfoSupportingItem) {
      await Promise.all(
        this.dependentInfoSupportingItemInput.dependents.map(async (d) =>
          this.individualProfileStore.fetchDependentProfileForJob(
            d.profileId,
            this.job.id,
          ),
        ),
      )
    }
  }

  /**
   * Deactivates the vm.
   */
  deactivate() {
    this._active.next(false)
  }

  /**
   * Updates the personal profile when remotes update happens.
   */
  private _onRemoteItemUpdated = task(async (ct: CancellationToken) => {
    if (this.dependentInfoSupportingItem?.completed) {
      await ct.race(this.fetchDependentProfiles())
    }
  })
}
